import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { profileApi, type KefuConfigResult } from "@/api";
import { auth } from "@/lib/auth";
import { storage, storageKeys } from "@/lib/storage";

const FAQ_LIST = [
  {
    id: 1,
    q: "技师迟到了怎么办？",
    a: "如技师超出预约时间 15 分钟未到达，您可联系客服协助处理。",
  },
  {
    id: 2,
    q: "如何申请退款？",
    a: "服务前可按订单规则取消，服务过程中如有问题也可联系平台客服处理。",
  },
  {
    id: 3,
    q: "技师资质如何保障？",
    a: "平台会按现有审核规则对技师信息、资质和服务能力进行管理。",
  },
  {
    id: 4,
    q: "上门服务需要准备什么？",
    a: "请保持服务环境整洁并预留足够空间，其他用品以实际服务说明为准。",
  },
  {
    id: 5,
    q: "可以指定技师吗？",
    a: "可以，在相关服务和技师页面选择后提交订单即可。",
  },
];

const DEFAULT_CUSTOMER_SERVICE: Required<KefuConfigResult> = {
  way: "1",
  name: "平台客服",
  remarks: "在线中，优先使用在线咨询联系平台客服",
  phone: "",
  service_phone: "",
  business_time: "",
  qr_code: "",
  enterprise_id: "",
  kefu_link: "",
};

interface StoredCustomerCity {
  id?: number;
  name?: string;
}

const getStoredCityId = () => {
  const storedCity = storage.get<StoredCustomerCity>(storageKeys.city);
  const cityId = Number(storedCity?.id || 0);
  return Number.isFinite(cityId) && cityId > 0 ? cityId : null;
};

const getDefaultAddressCityId = async () => {
  if (!auth.getToken()) {
    return null;
  }
  try {
    const addressResult = await profileApi.getAddressList();
    const addresses = Array.isArray(addressResult.usable) ? addressResult.usable : [];
    const address = addresses.find((item) => Number(item.is_default || 0) === 1) || addresses[0];
    const cityId = Number(address?.city_id || 0);
    return Number.isFinite(cityId) && cityId > 0 ? cityId : null;
  } catch {
    return null;
  }
};

const DEFAULT_AVATAR =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
      <defs>
        <linearGradient id="bg" x1="12%" y1="8%" x2="88%" y2="92%">
          <stop offset="0%" stop-color="#FFD8BF"/>
          <stop offset="55%" stop-color="#FFB07A"/>
          <stop offset="100%" stop-color="#FF8445"/>
        </linearGradient>
        <linearGradient id="coat" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#FFFDF8"/>
          <stop offset="100%" stop-color="#FFEAD6"/>
        </linearGradient>
      </defs>
      <rect width="80" height="80" rx="40" fill="url(#bg)"/>
      <circle cx="40" cy="27" r="13.5" fill="#FFF4EC"/>
      <path d="M28 24.5c1.2-6.8 5.8-10.5 12.3-10.5 6.9 0 11.5 4 12.1 11.2-2.5-2.4-5.1-3.4-7.9-3.4-2.2 0-4.7.8-7.3 2.3-2.7 1.5-5.1 1.8-9.2.4Z" fill="#8E4E2D"/>
      <circle cx="35.5" cy="28.5" r="1.35" fill="#8E4E2D"/>
      <circle cx="44.5" cy="28.5" r="1.35" fill="#8E4E2D"/>
      <path d="M36.7 34.4c1.3 1.3 5.3 1.3 6.6 0" stroke="#D97C52" stroke-width="1.8" stroke-linecap="round"/>
      <path d="M18 66c2.8-10.8 11.3-17.2 22-17.2S59.2 55.2 62 66" fill="url(#coat)"/>
      <path d="M28 51.8c2.4 3.3 6.8 5.4 12 5.4s9.6-2.1 12-5.4" stroke="#FFD2B2" stroke-width="2.1" stroke-linecap="round"/>
      <rect x="30.5" y="50.2" width="19" height="15" rx="9.5" fill="#FFF7F1" fill-opacity="0.88"/>
      <path d="M51.5 22.8c2.6 1.2 4.4 3.5 5 7.2" stroke="#8E4E2D" stroke-width="2.1" stroke-linecap="round"/>
      <circle cx="60" cy="60" r="10" fill="#FFFFFF" fill-opacity="0.18"/>
      <path d="M60 55v10M55 60h10" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round"/>
    </svg>
  `);

export default function ServicePage() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [calling, setCalling] = useState(false);
  const [serviceConfig, setServiceConfig] = useState<Required<KefuConfigResult>>(DEFAULT_CUSTOMER_SERVICE);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");
  const [qrPreviewOpen, setQrPreviewOpen] = useState(false);
  const qrCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      setLoadingConfig(true);
      setConfigError("");
      try {
        const cityId = getStoredCityId() || await getDefaultAddressCityId();
        const response = await profileApi.getKefuConfig(cityId);
        if (!mounted) {
          return;
        }
        setServiceConfig({
          way: response.way || DEFAULT_CUSTOMER_SERVICE.way,
          name: response.name || DEFAULT_CUSTOMER_SERVICE.name,
          remarks: response.remarks || DEFAULT_CUSTOMER_SERVICE.remarks,
          phone: response.phone || "",
          service_phone: response.service_phone || "",
          business_time: response.business_time || "",
          qr_code: response.qr_code || "",
          enterprise_id: response.enterprise_id || "",
          kefu_link: response.kefu_link || "",
        });
      } catch (error) {
        if (mounted) {
          setServiceConfig(DEFAULT_CUSTOMER_SERVICE);
          setConfigError("客服配置加载失败，请稍后再试");
        }
      } finally {
        if (mounted) {
          setLoadingConfig(false);
        }
      }
    };

    loadConfig();

    return () => {
      mounted = false;
    };
  }, []);

  const servicePhone = useMemo(
    () => serviceConfig.service_phone || serviceConfig.phone,
    [serviceConfig.phone, serviceConfig.service_phone],
  );
  const serviceWay = Number(serviceConfig.way || DEFAULT_CUSTOMER_SERVICE.way || 1);
  const businessTimeText = serviceConfig.business_time || "暂无服务时间";
  const onlineRemark = serviceConfig.remarks || DEFAULT_CUSTOMER_SERVICE.remarks;
  const qrCodeUrl = serviceConfig.qr_code || "";
  const headerRemark = useMemo(() => {
    if (serviceWay === 2) {
      return servicePhone ? "当前以电话客服为主，请在服务时间内拨打" : "后台暂未配置客服电话";
    }
    if (serviceWay === 3) {
      return "当前以在线客服为主，可直接进入在线会话";
    }
    return onlineRemark;
  }, [onlineRemark, servicePhone, serviceWay]);
  const descriptionText = useMemo(() => {
    if (serviceWay === 2) {
      return servicePhone ? `客服电话：${servicePhone}` : "后台暂未配置客服电话";
    }
    if (serviceWay === 3) {
      if (serviceConfig.enterprise_id) {
        return `在线客服 ID：${serviceConfig.enterprise_id}`;
      }
      return "点击进入在线客服会话";
    }
    return qrCodeUrl ? "客服二维码已配置，可直接扫码联系平台客服" : onlineRemark;
  }, [onlineRemark, qrCodeUrl, serviceConfig.enterprise_id, servicePhone, serviceWay]);
  const primaryAction = useMemo(() => {
    if (loadingConfig) {
      return {
        title: "正在加载",
        desc: "正在读取客服联系方式",
      };
    }
    if (serviceWay === 2) {
      return {
        title: "电话联系",
        desc: servicePhone ? "点击后直接拨打客服电话" : "后台暂未配置客服电话",
      };
    }
    if (serviceWay === 3) {
      return {
        title: "在线客服",
        desc: "点击进入在线客服会话",
      };
    }
    return {
      title: "扫码联系",
      desc: qrCodeUrl ? "请使用微信扫码联系平台客服" : "后台暂未配置客服二维码",
    };
  }, [loadingConfig, qrCodeUrl, serviceConfig.enterprise_id, serviceConfig.kefu_link, servicePhone, serviceWay]);
  const hasPrimaryAction = useMemo(() => {
    if (loadingConfig) {
      return false;
    }
    if (serviceWay === 2) {
      return Boolean(servicePhone);
    }
    if (serviceWay === 3) {
      return true;
    }
    return Boolean(qrCodeUrl);
  }, [loadingConfig, qrCodeUrl, servicePhone, serviceWay]);
  const primaryActionStatusText = loadingConfig ? "加载中" : (hasPrimaryAction ? "当前方式" : "未配置");

  const handleCall = () => {
    if (!servicePhone) {
      return;
    }
    setCalling(true);
    window.location.href = `tel:${servicePhone}`;
    window.setTimeout(() => setCalling(false), 2000);
  };

  const focusQrCard = () => {
    qrCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleQrCardClick = () => {
    if (qrCodeUrl) {
      setQrPreviewOpen(true);
    }
  };

  const handleOnlineService = () => {
    if (serviceWay === 2) {
      handleCall();
      return;
    }
    if (serviceWay === 3) {
      navigate("/support-chat");
      return;
    }
    if (qrCodeUrl) {
      focusQrCard();
      return;
    }
    if (serviceConfig.kefu_link) {
      window.open(serviceConfig.kefu_link, "_self");
    }
  };

  const handleEnterpriseService = () => {
    navigate("/support-chat");
  };

  const handleEnterpriseLink = () => {
    if (serviceConfig.kefu_link) {
      window.open(serviceConfig.kefu_link, "_self");
    }
  };

  const handleEmailCardClick = () => {
    if (serviceWay === 2) {
      handleCall();
      return;
    }
    if (serviceWay === 1 && qrCodeUrl) {
      focusQrCard();
      return;
    }
    if (serviceConfig.kefu_link) {
      window.open(serviceConfig.kefu_link, "_self");
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen pb-8"
      style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="relative overflow-hidden px-5 pt-12 pb-6"
        style={{ background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="flex items-center gap-3 relative z-10 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-arrow-left-s-line text-lg text-white" />
          </button>
          <h1 className="text-base font-bold text-white flex-1">联系客服</h1>
        </div>

        <div
          className="relative z-10 flex items-center gap-3 px-4 py-3.5 rounded-2xl"
          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
        >
          <div className="relative flex-shrink-0">
            <div
              className="w-10 h-10 rounded-full overflow-hidden"
              style={{ border: "2px solid rgba(255,255,255,0.6)" }}
            >
              <img
                src={DEFAULT_AVATAR}
                alt="客服"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <span
              className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full"
              style={{ background: "#4ADE80", border: "1.5px solid #fff" }}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">{serviceConfig.name || DEFAULT_CUSTOMER_SERVICE.name}</p>
            <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.65)" }}>{headerRemark}</p>
          </div>
          <span
            className="text-[10px] px-2.5 py-1 rounded-full font-semibold whitespace-nowrap"
            style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}
          >
            {businessTimeText}
          </span>
        </div>
      </div>

      <div className="px-5 pt-5 space-y-3">
        {(loadingConfig || hasPrimaryAction) && (
        <button
          className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer active:opacity-80 transition-all"
          style={{
            background: "#fff",
            border: hasPrimaryAction ? "1.5px solid #FFD4A0" : "1px solid #F0EAE0",
            opacity: hasPrimaryAction ? 1 : 0.7,
          }}
          onClick={handleOnlineService}
          disabled={!hasPrimaryAction}
        >
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF4EC" }}>
              <i className="ri-chat-smile-3-line text-xl" style={{ color: "#FF6018" }} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{primaryAction.title}</p>
              <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
                {primaryAction.desc}
              </p>
            </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-[10px] px-2 py-1 rounded-full font-semibold"
              style={{ background: hasPrimaryAction ? "#EDFBF4" : "#F5F2EE", color: hasPrimaryAction ? "#17A87B" : "#A99F94" }}
            >
              {primaryActionStatusText}
            </span>
            <i className="ri-arrow-right-s-line text-lg" style={{ color: "#D0C8BE" }} />
          </div>
        </button>
        )}

        {configError && (
          <div
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs"
            style={{ background: "#FFF7E8", color: "#B56700", border: "1px solid #FFE0A8" }}
          >
            <i className="ri-error-warning-line text-base flex-shrink-0" />
            <span>{configError}</span>
          </div>
        )}

        <button
          onClick={handleCall}
          className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer active:opacity-80"
          style={{ background: "#fff", border: "1px solid #F0EAE0", opacity: servicePhone ? 1 : 0.7 }}
          disabled={!servicePhone}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF4EC" }}>
            <i className={`${calling ? "ri-phone-fill" : "ri-phone-line"} text-xl`} style={{ color: "#FF6018" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>电话客服</p>
            <p className="text-[11px] mt-0.5" style={{ color: calling ? "#FF6018" : "#BBAAA0" }}>
              {calling
                ? `正在拨号 ${servicePhone || "暂无电话"}...`
                : `${servicePhone || "暂无客服电话"}${serviceConfig.business_time ? ` · ${serviceConfig.business_time}` : ""}`}
            </p>
          </div>
          <i className="ri-arrow-right-s-line text-lg flex-shrink-0" style={{ color: "#D0C8BE" }} />
        </button>

        {qrCodeUrl && (
        <div
          ref={qrCardRef}
          role="button"
          onClick={handleQrCardClick}
          className="flex items-center gap-4 p-4 rounded-2xl cursor-pointer active:opacity-80"
          style={{ background: "#fff", border: "1px solid #F0EAE0" }}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#EDFBF5" }}>
            <i className="ri-wechat-line text-xl" style={{ color: "#17B978" }} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>微信客服</p>
            <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
              扫码添加专属客服微信
            </p>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ background: "#F5F2EE", border: "1px solid #EDE8E0" }}
          >
            <img src={qrCodeUrl} alt="客服二维码" className="w-full h-full object-cover" />
          </div>
        </div>
        )}

        <button
          type="button"
          onClick={handleEnterpriseService}
          className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer active:opacity-80"
          style={{ background: "#fff", border: "1px solid #F0EAE0" }}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#EEF3FF" }}>
            <i className="ri-customer-service-2-line text-xl" style={{ color: "#4D6FEA" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>在线客服</p>
            <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
              点击进入在线客服会话
            </p>
          </div>
          <i className="ri-arrow-right-s-line text-lg flex-shrink-0" style={{ color: "#D0C8BE" }} />
        </button>

        {serviceConfig.kefu_link && (
        <button
          type="button"
          onClick={handleEnterpriseLink}
          className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer active:opacity-80"
          style={{ background: "#fff", border: "1px solid #F0EAE0" }}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#EEF3FF" }}>
            <i className="ri-wechat-channels-line text-xl" style={{ color: "#4D6FEA" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>企业客服</p>
            <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
              点击进入企业微信客服
            </p>
          </div>
          <i className="ri-arrow-right-s-line text-lg flex-shrink-0" style={{ color: "#D0C8BE" }} />
        </button>
        )}

        <button
          type="button"
          onClick={handleEmailCardClick}
          className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer"
          style={{ background: "#fff", border: "1px solid #F0EAE0" }}
        >
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF8E7" }}>
            <i className="ri-mail-send-line text-xl" style={{ color: "#E8860A" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>客服说明</p>
            <p className="text-[11px] mt-0.5 truncate" style={{ color: "#BBAAA0" }}>{descriptionText}</p>
          </div>
          <i className="ri-arrow-right-s-line text-lg flex-shrink-0" style={{ color: "#D0C8BE" }} />
        </button>
      </div>

      <div className="px-5 pt-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#FF6018,#FF9A3C)" }} />
          <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>常见问题</span>
        </div>
        <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid #F0EAE0" }}>
          {FAQ_LIST.map((faq, i) => (
            <div
              key={faq.id}
              className={i < FAQ_LIST.length - 1 ? "border-b" : ""}
              style={{ borderColor: "#F8F5F1" }}
            >
              <button
                onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer text-left"
              >
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                  style={{ background: "#FFF4EC", color: "#FF6018" }}
                >
                  Q
                </div>
                <span className="flex-1 text-sm font-medium" style={{ color: "#1A1A1A" }}>{faq.q}</span>
                {expandedFaq === faq.id
                  ? <i className="ri-arrow-up-s-line text-base flex-shrink-0" style={{ color: "#C0B8AE" }} />
                  : <i className="ri-arrow-down-s-line text-base flex-shrink-0" style={{ color: "#C0B8AE" }} />
                }
              </button>
              {expandedFaq === faq.id && (
                <div
                  className="px-4 pb-4 flex gap-3"
                >
                  <div
                    className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5"
                    style={{ background: "#EDFBF4", color: "#17A87B" }}
                  >
                    A
                  </div>
                  <p className="text-xs leading-relaxed flex-1" style={{ color: "#777" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4">
        <button
          onClick={() => navigate("/feedback")}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl cursor-pointer"
          style={{ background: "#F8F6F3", border: "1px solid #EDE8E0" }}
        >
          <i className="ri-message-3-line text-base flex-shrink-0" style={{ color: "#9B72CF" }} />
          <span className="text-sm flex-1 text-left" style={{ color: "#666" }}>没找到答案？前往提交建议反馈</span>
          <i className="ri-arrow-right-s-line text-base flex-shrink-0" style={{ color: "#C0B8AE" }} />
        </button>
      </div>

      {qrPreviewOpen && qrCodeUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-8"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={() => setQrPreviewOpen(false)}
        >
          <div
            className="w-full max-w-[300px] rounded-2xl p-5 text-center"
            style={{ background: "#fff" }}
            onClick={(event) => event.stopPropagation()}
          >
            <p className="text-sm font-bold mb-3" style={{ color: "#1A1A1A" }}>微信扫码联系客服</p>
            <img src={qrCodeUrl} alt="客服二维码" className="w-56 h-56 object-contain mx-auto rounded-xl" />
            <p className="text-xs mt-3" style={{ color: "#8A8076" }}>长按图片可保存或转发</p>
            <button
              type="button"
              className="mt-4 w-full h-10 rounded-xl text-sm font-semibold"
              style={{ background: "#FF6018", color: "#fff" }}
              onClick={() => setQrPreviewOpen(false)}
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
